/* eslint-disable jsx-a11y/click-events-have-key-events */

/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useEffect, useState } from 'react';
import { IoIosHelpCircleOutline } from 'react-icons/io';
import { Tooltip } from 'react-tooltip';
import { Button } from 'primereact/button';

import { metricValueFormat } from '../../utils/metricValueFormat';
import MetricProgressDirection from '../metricProgressDirection/metricProgressDirection';
import './metricMonthlySummaryCard.scss';
import trendDown from '../../assets/trendDown.svg';
import trendUp from '../../assets/trendUp.svg';
import { MetricColors, OmittedMetricColor, TieredMetricColors } from './GradientDefs';
import { OverridePerformancePopup } from '../coachingActionPlan/overridePerformancePopup/OverridePerformancePopup';
import editIcon from '../../assets/editIcon.png';
import { CoachingTabs } from '../../utils/typeConstants';
import { MetricGaugeVisual, GaugeVariant } from '../metricGaugeVisual/MetricGaugeVisual';
import { MetricTargetTier, getMeetsTarget, getExceedsTarget, getTieredStatusVisual } from '../../utils/metricAttainment';
import { TieredTargetIcon, TieredCircleCheckIcon, TieredCircleXIcon } from './tieredMetricIcons';

type metricProps = {
    metricNameVal?: string;
    targetVal: number;
    metricTypeName: string;
    metricAttained?: boolean;
    metricDesc?: string;
    metricVal: number;
    metricDirectionId?: number;
    selectedMetric?: string;
    onCardClick?: (metricName: string, index: number) => void;
    index?: number;
    metricOmitted?: boolean;
    overrideInfo?: {
        label: string;
        value: string;
        description: string;
    };
    via?: number;
    metricTypeId?: number;
    CAPCurrentSession?: any;
    onBeforeOverride?: () => void;
    // Tiered (P4P multi-tier) props - all optional, default to the legacy gauge when absent.
    // variant is explicit and must be driven by scorecard_version.scorecard_calculation_rule_id
    // upstream - never inferred from whether targets/attainmentRank happen to be present.
    // attainmentRank (not metric_attainment_level_id) drives all status/rendering logic so a
    // future tier addition or lookup-table reshuffle doesn't require touching this component.
    variant?: GaugeVariant;
    attainmentRank?: number | null;
    attainmentLevelLabel?: string | null;
    targets?: MetricTargetTier[] | null;
    // min/max come straight from the API, already oriented "worst->best" per metric_direction_id -
    // needed to compute the 4-arc gauge's Reference/Exceeds/Meets/Value positions.
    min?: number;
    max?: number;
};
export const MetricMonthlySummaryCard = ({
    metricNameVal,
    targetVal,
    metricVal,
    metricTypeName,
    metricAttained,
    metricDesc,
    metricDirectionId,
    selectedMetric,
    onCardClick,
    index,
    metricOmitted,
    overrideInfo,
    via,
    metricTypeId,
    CAPCurrentSession,
    onBeforeOverride,
    variant = 'legacy',
    attainmentRank,
    attainmentLevelLabel,
    targets,
    min,
    max,
}: metricProps) => {
    const [target, setTarget] = useState('');
    const [strMetricValue, setStrMetricValue] = useState('');
    const [showOverride, setShowOverride] = useState<boolean>(false);
    const [selectedMetricBackgrndColor, setSelectedMetricBackgrndColor] = useState('#000000');

    useEffect(() => {
        const colorIndex = metricOmitted ? 5 : (index ?? 0) % MetricColors.length;
        const colors = MetricColors[colorIndex] ?? MetricColors[0];
        setSelectedMetricBackgrndColor(colors.bgColor);
        setTarget('');
        setStrMetricValue('');
        const formatAndSetTarget = () => {
            if (targetVal === null) {
                setTarget('--');
            } else {
                const targetValue = metricValueFormat(targetVal, metricTypeName);
                setTarget(targetValue);
            }
        };

        const formatAndSetMetricValue = () => {
            if (metricVal === null) {
                setStrMetricValue('--');
            }
            // else if (metricVal >= 0) {
            else {
                const metricValue = metricValueFormat(metricVal, metricTypeName);
                setStrMetricValue(metricValue);
            }
        };

        formatAndSetTarget();
        formatAndSetMetricValue();
    }, [metricTypeName, metricVal, metricNameVal, targetVal, metricOmitted, index]);

    //cPathColour = metricAttained === true ? 'linear-gradient(308.09deg, #52D5FF 14.91%, #0047FF 77%)' : '#F7D040';
    const colorIndex = metricOmitted ? 5 : (index ?? 0) % MetricColors.length;
    const meetsTarget = targets ? getMeetsTarget(targets) : null;
    const exceedsTarget = targets ? getExceedsTarget(targets) : null;
    const gaugeVisual = (
        <MetricGaugeVisual
            variant={variant}
            valueText={strMetricValue}
            colorIndex={colorIndex}
            metricOmitted={metricOmitted}
            value={metricVal}
            target={targetVal}
            metricAttained={metricAttained}
            attainmentRank={attainmentRank}
            attainmentLevelLabel={attainmentLevelLabel}
            metricValue={metricVal}
            min={min}
            max={max}
            meetsTarget={meetsTarget}
            exceedsTarget={exceedsTarget}
        />
    );
    const handleCardClick = () => {
        if (metricNameVal && onCardClick) {
            onCardClick(metricNameVal, index ?? 0);
        }
    };

    // Status line. Legacy keeps its solid pi chip + met/not-met classes untouched. Tiered uses the
    // outline circle-check / circle-x icons and the shared getTieredStatusVisual color/icon,
    // keyed primarily off metric_attainment_level_label (per design spec) so this can never
    // disagree with the gauge's own center-value color, which uses the same helper.
    const isTiered = variant === 'tiered';
    const tieredVisual = getTieredStatusVisual(attainmentLevelLabel, attainmentRank);
    const tieredStatusColor = metricOmitted ? '#3F5765' : tieredVisual.color;
    const tieredStatusLabel = attainmentLevelLabel ?? '--';

    const legacyStatusIconClass = metricAttained ? 'pi pi-check metricMetIcon' : 'pi pi-times metricNotMetIcon';
    const legacyStatusTextClass = metricAttained ? 'metricMet' : 'MetricNotMet';
    const legacyStatusLabelText = metricAttained ? 'Metric Met' : 'Metric Not Met';

    // Precomputed to avoid a nested ternary in JSX (matches this file's no-nested-ternary style).
    let statusContent: React.ReactNode;
    if (metricOmitted) {
        statusContent = isTiered ? (
            <span className="MetricOmitted" style={{ color: OmittedMetricColor }}>
                <i className="pi pi-minus" style={{ marginRight: 6 }} />
                Omitted Metric
            </span>
        ) : (
            <span className="MetricOmitted">Omitted Metric</span>
        );
    } else if (isTiered) {
        statusContent = (
            <span className="metricTieredStatus" style={{ color: tieredStatusColor }}>
                {tieredVisual.icon === 'cross' ? <TieredCircleXIcon /> : <TieredCircleCheckIcon />}
                <span className="metricTieredStatus__label">{tieredStatusLabel}</span>
            </span>
        );
    } else {
        statusContent = (
            <>
                <i className={legacyStatusIconClass} />
                <span className={legacyStatusTextClass}>{legacyStatusLabelText}</span>
            </>
        );
    }

    const meetsTargetText = meetsTarget !== null ? metricValueFormat(meetsTarget, metricTypeName) : '--';
    const exceedsTargetText = exceedsTarget !== null ? metricValueFormat(exceedsTarget, metricTypeName) : '--';
    const tieredColorPair = TieredMetricColors[colorIndex] ?? TieredMetricColors[0];

    return (
        <div
            className="col"
            onClick={handleCardClick}
            role="button"
            tabIndex={0}
            style={{
                cursor: 'hand',
                //  borderBottom: selectedMetric === metricNameVal ? 'none' : '1px solid lightgray',
                // borderLeft: '1px solid lightgray',
                border: '1px solid lightgray',
                paddingLeft: '0px', paddingRight: '0px', height: '100%',
            }}
        >
            <div className="rounded h-100" style={{
                backgroundColor: selectedMetric === metricNameVal ? selectedMetricBackgrndColor : 'transparent',
                cursor: 'pointer',
            }}>
                <div
                    className="card-body"
                    style={{
                        backgroundColor: selectedMetric === metricNameVal ? selectedMetricBackgrndColor : 'transparent',
                        cursor: 'pointer',
                    }}
                >
                    <div className="row">
                        <div className="col-11 align-top text-center p-0">
                            <h6 className="card-title metricName mb-0 align-top" data-testid={metricNameVal}>{metricNameVal}</h6>

                            <div>
                                {metricDirectionId && (<>
                                    <img src={metricDirectionId === 1 ? trendUp : trendDown} alt="trendUp" style={{ verticalAlign: 'sub', height: '18px' }} />
                                    <span className='metricDirection'>{metricDirectionId === 1 ? 'Ascending' : 'Descending'}</span></>
                                )}
                            </div>
                        </div>
                        <div className="col-1 p-0">
                            <IoIosHelpCircleOutline
                                data-tooltip-id={metricNameVal}
                                data-tooltip-content={metricDesc}
                                style={{ cursor: 'pointer' }}
                            />
                            {metricDesc && (
                                <Tooltip
                                    id={metricNameVal}
                                    closeOnEsc
                                    className="rounded tooltip"
                                    events={['hover']}
                                />
                            )}
                        </div>
                    </div>

                    <div className="row d-flex justify-content-center align-items-center">
                        {/* <div className="col-6">&nbsp;</div> */}
                        {/* <div className="col-6">{metricDirectionId && metricDirectionView()}</div> */}
                        {/* <div className="col-6 d-flex justify-content-center" style={{maxHeight:'150px',maxWidth:'150px'}}>{circularprogressbar}</div> */}
                        <div
                            className="d-flex justify-content-center align-items-center"
                            style={{
                                width: variant === 'tiered' ? '200px' : '135px',
                                height: variant === 'tiered' ? '200px' : '135px',
                                padding: '3px',
                                boxSizing: 'border-box',
                                margin: '0 auto',
                                marginBottom: variant === 'tiered' ? '-85px' : '0',
                                position: 'relative',
                            }}
                        >
                            {gaugeVisual}
                            {overrideInfo && (
                                <>
                                    <i
                                        className="pi pi-info-circle"
                                        data-tooltip-id={`${metricNameVal}-override`}
                                        style={{
                                            position: 'absolute',
                                            top: '4px',
                                            right: '4px',
                                            cursor: 'pointer',
                                            fontSize: '30px',
                                            color: '#C0392B',
                                            backgroundColor: '#FADBD8',
                                            borderRadius: '50%',
                                            padding: '4px',
                                        }}
                                    />
                                    <Tooltip
                                        id={`${metricNameVal}-override`}
                                        className="rounded tooltip override-tooltip"
                                        events={['click']}
                                        place="right-start"
                                        closeOnEsc
                                        positionStrategy="fixed"
                                        style={{ overflow: 'visible', maxHeight: 'none', zIndex: 99999 }}
                                    >
                                        <div className="override-tooltip__content">
                                            <span className="override-tooltip__label">{overrideInfo.label}</span>
                                            <span className="override-tooltip__value">{overrideInfo.value}</span>
                                            <span className="override-tooltip__description">{overrideInfo.description}</span>
                                        </div>
                                    </Tooltip>
                                </>
                            )}
                            {via === CoachingTabs.CoachingActionPlan && (
                                <div style={{
                                    display: 'flex', alignItems: 'center', top: '0px',
                                    left: '130px', position: 'absolute'
                                }}>
                                    <Button
                                        className="p-button-rounded p-button-text p-button-sm"
                                        onClick={async () => {
                                            await onBeforeOverride?.();
                                            setShowOverride(true);
                                        }}
                                        data-tooltip-id={`${metricNameVal}-edit-button`}
                                    >
                                        <img src={editIcon} alt="edit" style={{ width: '16px', height: '16px' }} />
                                    </Button>
                                    <Tooltip
                                        id={`${metricNameVal}-edit-button`}
                                        className="rounded tooltip edit-tooltip"
                                        events={['hover']}
                                        place="top"
                                        closeOnEsc
                                        edit-tooltip
                                    >
                                        <div style={{ maxWidth: '380px' }}>
                                            If you believe the metric value displayed is not correct,
                                            please log an Override of the metric for annotation.
                                        </div>
                                    </Tooltip>
                                </div>
                            )}

                        </div>
                    </div>
                    <div className='row mt-4'>
                        <div className="col-12 text-center">
                            {statusContent}
                        </div>
                    </div>
                    <div className="row mt-1">
                        <div className="col-12">
                            { !metricOmitted && variant === 'tiered'  && (
                                <>
                                    <hr className="metricTieredDivider" />
                                    <div className="metricTargetThresholds text-center">
                                        <div className="metricTargetThresholds__row" style={{ color: tieredColorPair.dark }}>
                                            <TieredTargetIcon />
                                            <span>Meets at: {meetsTargetText}</span>
                                        </div>
                                        <div className="metricTargetThresholds__row" style={{ color: tieredColorPair.light }}>
                                            <TieredTargetIcon />
                                            <span>Exceeds at: {exceedsTargetText}</span>
                                        </div>
                                    </div>
                                </>
                            )}
                            { metricOmitted && variant === 'tiered'  && (
                                <>
                                    <hr className="metricTieredDivider" />
                                    <div className="metricTargetThresholds text-center">
                                        <div className="metricTargetThresholds__row" style={{ color: OmittedMetricColor}}>
                                            <TieredTargetIcon />
                                            <span>Meets at: {meetsTargetText}</span>
                                        </div>
                                        <div className="metricTargetThresholds__row" style={{ color: OmittedMetricColor }}>
                                            <TieredTargetIcon />
                                            <span>Exceeds at: {exceedsTargetText}</span>
                                        </div>
                                    </div>
                                </>
                            )}
                            {!metricOmitted && variant === 'legacy' && metricDirectionId && target && (
                                <MetricProgressDirection
                                    metricDirectionId={metricDirectionId}
                                    trendPercentage={target}
                                    sType=''
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <OverridePerformancePopup
                visible={showOverride}
                metricName={metricNameVal}
                currentPerformance={CAPCurrentSession?.metricCurrentValue}
                metricTypeId={metricTypeId}
                metricTypeName={metricTypeName}
                recognize={false}
                CAPCurrentSession={CAPCurrentSession}
                onCancel={() => setShowOverride(false)}

            />
        </div>
    );
};
